import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLenis } from "lenis/react";
import { Menu, X, ArrowUpRight } from "lucide-react";
import BookingDialog from "./BookingDialog";

const LINKS = [
  { label: "Expertise", href: "#expertise" },
  { label: "Results", href: "#results" },
  { label: "Services", href: "#services" },
  { label: "Process", href: "#process" },
  { label: "Work", href: "#work" },
  { label: "Journal", href: "#insights" },
];

const Nav = () => {
  const [open, setOpen] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const lenis = useLenis();

  const go = (href) => {
    setOpen(false);
    lenis?.scrollTo(href, { offset: -72, duration: 1.4 });
  };

  return (
    <header
      data-testid="main-nav"
      className="fixed top-0 left-0 right-0 z-50 border-b border-line bg-paper/70 backdrop-blur-xl"
    >
      <div className="mx-auto flex max-w-[100rem] items-center justify-between px-6 md:px-12 py-4">
        <button
          data-testid="nav-logo"
          onClick={() => go("#top")}
          className="text-left leading-none"
        >
          <span className="font-serif text-xl md:text-2xl font-medium tracking-tight">
            Makkar
          </span>
          <span className="block text-[10px] uppercase tracking-[0.3em] text-ash mt-1">
            Growth Partners
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              onClick={() => go(l.href)}
              className="text-xs uppercase tracking-[0.2em] text-ash hover:text-ink transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <BookingDialog
            trigger={
              <button
                data-testid="nav-book-call-btn"
                className="group hidden sm:inline-flex items-center gap-2 bg-ink text-paper text-xs uppercase tracking-[0.2em] px-6 py-3 hover:bg-taupe transition-colors duration-300"
              >
                Book a Call
                <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            }
          />
          <button
            data-testid="nav-mobile-menu-btn"
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.76, 0, 0.24, 1] }}
            className="lg:hidden overflow-hidden border-t border-line bg-paper"
          >
            <div className="flex flex-col px-6 py-6 gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  onClick={() => go(l.href)}
                  className="text-left font-serif text-2xl font-light"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="nav-mobile-book-call-btn"
                onClick={() => { setOpen(false); setBookingOpen(true); }}
                className="mt-2 inline-flex items-center justify-center gap-2 bg-ink text-paper text-xs uppercase tracking-[0.2em] px-6 py-4"
              >
                Book a Call <ArrowUpRight size={14} />
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>

      <BookingDialog open={bookingOpen} onOpenChange={setBookingOpen} />
    </header>
  );
};

export default Nav;
